import { useQueries } from '@tanstack/react-query'
import { useEffect } from 'react'
import { message } from 'antd'
import {
  loadGlonassoftData,
  GlonassoftSourceCredentials,
  SourceApiResponse,
} from '@/shared/api/sourceApi'
import {
  SourceVehicle,
  SourceDriver,
  SourceUser,
  SourceGeozone,
  SourceRetranslation,
} from '@/app/types/source'
import { getSessionAbortSignal } from '@/shared/api/sessionAbortController'

const STALE_TIME = 5 * 60 * 1000

export interface GlonassoftSourceData {
  vehicles: SourceApiResponse<SourceVehicle> | undefined
  drivers: SourceApiResponse<SourceDriver> | undefined
  users: SourceApiResponse<SourceUser> | undefined
  geozones: SourceApiResponse<SourceGeozone> | undefined
  retranslations: SourceApiResponse<SourceRetranslation> | undefined
  isLoading: boolean
  isError: boolean
}

export const useGlonassoftSourceQueries = (
  agentId: string,
  credentials: GlonassoftSourceCredentials | null,
  sourceApiUrl: string,
  systemUrl: string
): GlonassoftSourceData => {
  const enabled = !!agentId && !!credentials
  const credentialsWithAgent = credentials ? { ...credentials, agentId } : null

  const [vehicles, drivers, users, geozones, retranslations] = useQueries({
    queries: [
      {
        queryKey: ['glonassoft-source', 'Vehicles', agentId, credentials],
        queryFn: () =>
          loadGlonassoftData<SourceVehicle>(
            'Vehicles',
            credentialsWithAgent!,
            sourceApiUrl,
            systemUrl,
            getSessionAbortSignal()
          ),
        enabled,
        staleTime: STALE_TIME,
      },
      {
        queryKey: ['glonassoft-source', 'Drivers', agentId, credentials],
        queryFn: () =>
          loadGlonassoftData<SourceDriver>(
            'Drivers',
            credentialsWithAgent!,
            sourceApiUrl,
            systemUrl,
            getSessionAbortSignal()
          ),
        enabled,
        staleTime: STALE_TIME,
      },
      {
        queryKey: ['glonassoft-source', 'Users', agentId, credentials],
        queryFn: () =>
          loadGlonassoftData<SourceUser>(
            'Users',
            credentialsWithAgent!,
            sourceApiUrl,
            systemUrl,
            getSessionAbortSignal()
          ),
        enabled,
        staleTime: STALE_TIME,
      },
      {
        queryKey: ['glonassoft-source', 'Geozones', agentId, credentials],
        queryFn: () =>
          loadGlonassoftData<SourceGeozone>(
            'Geozones',
            credentialsWithAgent!,
            sourceApiUrl,
            systemUrl,
            getSessionAbortSignal()
          ),
        enabled,
        staleTime: STALE_TIME,
      },
      {
        queryKey: ['glonassoft-source', 'Retranslations', agentId, credentials],
        queryFn: () =>
          loadGlonassoftData<SourceRetranslation>(
            'Retranslations',
            credentialsWithAgent!,
            sourceApiUrl,
            systemUrl,
            getSessionAbortSignal()
          ),
        enabled,
        staleTime: STALE_TIME,
      },
    ],
  })

  useEffect(() => {
    if (vehicles.data?.error) message.error(vehicles.data.error)
  }, [vehicles.data])

  useEffect(() => {
    if (drivers.data?.error) message.error(drivers.data.error)
  }, [drivers.data])

  useEffect(() => {
    if (users.data?.error) message.error(users.data.error)
  }, [users.data])

  useEffect(() => {
    if (geozones.data?.error) message.error(geozones.data.error)
  }, [geozones.data])

  useEffect(() => {
    if (retranslations.data?.error) message.error(retranslations.data.error)
  }, [retranslations.data])

  return {
    vehicles: vehicles.data,
    drivers: drivers.data,
    users: users.data,
    geozones: geozones.data,
    retranslations: retranslations.data,
    isLoading: [vehicles, drivers, users, geozones, retranslations].some(
      (r) => r.isLoading
    ),
    isError: [vehicles, drivers, users, geozones, retranslations].some(
      (r) => r.isError
    ),
  }
}
